/**
 * Common utility for calculating custom metrics from system metrics
 * Used across Dashboard Matrix Analytics, Dynamic Chart, and Table components
 */

// All system metric keys that can be used in custom metric formulas
const ALL_SYSTEM_METRIC_KEYS = [
  "totalSignup", "totalSignout", "accountDeleted", "totalActiveUsers",
  "avgActiveTime", "firstConsultation", "repeatConsultation",
  "totalPaidChatConsultation", "totalPaidCallConsultation",
  "firstCallPaidConsultation", "secondCallPaidConsultation", "thirdCallPaidConsultation",
  "fourthCallPaidConsultation", "fifthCallPaidConsultation", "sixthCallPaidConsultation",
  "seventhCallPaidConsultation", "firstChatPaidConsultation", "secondChatPaidConsultation",
  "thirdChatPaidConsultation", "fourthChatPaidConsultation", "fifthChatPaidConsultation",
  "sixthChatPaidConsultation", "seventhChatPaidConsultation",
  "freeCanceledChatConsultation", "freeCancelledCallConsultation",
  "paidMissedCallConsultation", "paidMissedChatConsultation",
  "noOfFirstRecharge", "totalNoOfRecharge", "firstRechargeAmount", "totalRechargeAmount",
  "spendOnCall", "spendOnChat", "totalCoinsInWallet", "timeSpentOnChat", "timeSpentOnCall"
];

/**
 * Calculate custom metric value from a formula and system metrics data
 * @param {string} formula - The formula string (e.g., "totalSignup + totalSignout")
 * @param {object} systemMetricsData - Object containing system metric values
 * @returns {number} - Calculated value
 */
export const calculateCustomMetricValue = (formula, systemMetricsData) => {
  try {
    if (!formula || !systemMetricsData) return 0;
    
    // Replace system metric keys with their values
    let expression = formula;
    
    ALL_SYSTEM_METRIC_KEYS.forEach((key) => {
      const value = systemMetricsData[key] || 0;
      const regex = new RegExp(`\\b${key}\\b`, "g");
      expression = expression.replace(regex, value);
    });
    
    // Evaluate the expression safely
    try {
      const result = Function('"use strict"; return (' + expression + ')')();
      return isNaN(result) || !isFinite(result) ? 0 : result;
    } catch (e) {
      console.error("Error evaluating formula:", e, "Formula:", formula, "Expression:", expression);
      return 0;
    }
  } catch (error) {
    console.error("Error calculating custom metric:", error);
    return 0;
  }
};

/**
 * Calculate custom metric value for a specific date row
 * This is an alias for calculateCustomMetricValue for clarity
 * @param {string} formula - The formula string
 * @param {object} dateRowData - Object containing system metric values for a specific date
 * @returns {number} - Calculated value for that date
 */
export const calculateCustomMetricForDate = (formula, dateRowData) => {
  return calculateCustomMetricValue(formula, dateRowData);
};

/**
 * Get all system metric keys
 * @returns {Array<string>} - Array of all system metric keys
 */
export const getAllSystemMetricKeys = () => {
  return [...ALL_SYSTEM_METRIC_KEYS];
};

/**
 * Extract system metrics used in a custom metric formula
 * @param {string} formula - The formula string
 * @returns {Set<string>} - Set of system metric keys used in the formula
 */
export const extractSystemMetricsFromFormula = (formula) => {
  const usedKeys = new Set();
  
  if (!formula) return usedKeys;
  
  ALL_SYSTEM_METRIC_KEYS.forEach((key) => {
    const regex = new RegExp(`\\b${key}\\b`, "g");
    if (regex.test(formula)) {
      usedKeys.add(key);
    }
  });
  
  return usedKeys;
};

